import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Button,
  ButtonVariant,
  Icon,
  IconNames
} from "@wattpad/web-ui-library";
import classNames from "classnames";
import RemovePollModal from "./RemovePollModal";
import { useTrans } from "../../../hooks";
import postPoll from "../../../../helpers/polls/client.post-poll";
import getPoll from "../../../../helpers/polls/client.get-writer-poll";
import deletePoll from "../../../../helpers/polls/client.delete-poll";
import ReaderPoll from "../../../shared-components/ReaderPoll";
import { sanitizeHTMLExceptQuotes } from "../../../helpers/sanitizeHTML";

export const POLL_STATE = {
  ADD_POLL: 1,
  EDITING_POLL: 2,
  VIEW_POLL: 3
};

const POLL_TITLE_CHAR_MAX = 200;
const POLL_TITLE_CHAR_MIN = 3;
const POLL_OPTION_CHAR_MAX = 140;
const POLL_OPTION_CHAR_MIN = 1;

const URL_REGEX = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/[a-zA-Z0-9]+\.[^\s]{2,}|[a-zA-Z0-9]+\.[^\s]{2,})/gi;

const WriterPoll = ({ storyId, partId }) => {
  const { trans } = useTrans();
  const errorMessage = trans("Something went wrong. Please try again");
  const [pollState, setPollState] = useState();
  const [pollOptions, setPollOptions] = useState([
    { label: trans("Answer %s", 1) },
    { label: trans("Answer %s", 2) }
  ]);
  const [pollIsValid, setPollIsValid] = useState(false);
  const [pollTitle, setPollTitle] = useState();
  const [showRemoveModal, setShowRemoveModal] = useState();
  const [viewPollData, setViewPollData] = useState();
  const [pollTitleError, setPollTitleError] = useState();
  const [pollOptionError, setPollOptionError] = useState();

  useEffect(() => {
    getPoll(storyId, partId).then(data => {
      if (data.polls.length > 0) {
        setPollState(POLL_STATE.VIEW_POLL);
        setViewPollData(data.polls[0]);
      } else {
        setPollState(POLL_STATE.ADD_POLL);
      }
    });
  }, []);

  useEffect(
    () => {
      let valid = true;
      for (let i = 0; i < pollOptions.length; i++) {
        if (!pollOptions[i].valid) {
          valid = false;
        }
      }
      setPollIsValid(
        valid &&
          pollTitle?.length >= POLL_TITLE_CHAR_MIN &&
          pollTitle?.length <= POLL_TITLE_CHAR_MAX
      );
    },
    [pollOptions, pollTitle]
  );

  const onPollOptionChange = (option, value) => {
    let newPollOptions = pollOptions.slice();
    if (option.error) {
      setPollOptionError("");
    }
    newPollOptions[option] = {
      label: newPollOptions[option].label,
      value: value,
      valid:
        value.length >= POLL_OPTION_CHAR_MIN &&
        value.length <= POLL_OPTION_CHAR_MAX,
      charOverflowCount: value.length - POLL_OPTION_CHAR_MAX,
      error: false
    };
    setPollOptions(newPollOptions);
  };

  const onPollOptionRemove = option => {
    let newPollOptions = pollOptions.slice();
    newPollOptions.splice(option, 1);
    setPollOptions(newPollOptions);
  };

  const clearPollData = () => {
    setPollState(POLL_STATE.ADD_POLL);
    setPollOptions([
      { label: trans("Answer %s", 1) },
      { label: trans("Answer %s", 2) }
    ]);
    setPollTitle("");
    setPollIsValid(false);
    setShowRemoveModal(false);
    setPollTitleError("");
    setPollOptionError("");
  };

  const handleAddPoll = () => {
    const sanitizedPollTitle = sanitizeHTMLExceptQuotes(pollTitle);

    if (URL_REGEX.test(sanitizedPollTitle)) {
      setPollTitleError(trans("Polls may not include URLs"));
      return;
    }

    let optionError;

    const validatedPollOptions = pollOptions.map(option => {
      const sanitizedOptionValue = sanitizeHTMLExceptQuotes(option.value);

      if (URL_REGEX.test(sanitizedOptionValue)) {
        optionError = trans("Polls may not include URLs");
        return {
          ...option,
          error: true
        };
      } else return option;
    });

    if (optionError) {
      setPollOptions(validatedPollOptions);
      setPollOptionError(optionError);
      return;
    }

    const options = pollOptions.map(option => ({
      text: sanitizeHTMLExceptQuotes(option.value)
    }));

    postPoll(storyId, partId, "singleAnswerPoll", sanitizedPollTitle, options)
      .then(data => {
        setPollState(POLL_STATE.VIEW_POLL);
        setViewPollData(data);
      })
      .catch(() => {
        wattpad.utils.showToast(errorMessage, { type: "dismissable" });
      });
  };

  const handleDeletePoll = () => {
    deletePoll(storyId, partId, viewPollData.id)
      .then(() => {
        clearPollData();
      })
      .catch(() => {
        wattpad.utils.showToast(errorMessage, { type: "dismissable" });
      });
  };

  const handleKeyDown = event => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  if (pollState === POLL_STATE.ADD_POLL) {
    return (
      <Button
        variant={ButtonVariant.SECONDARY}
        label={trans("Add a poll")}
        ariaLabel={trans("Add a poll")}
        leadingIcon={IconNames.WpAddPoll}
        onClick={() => setPollState(POLL_STATE.EDITING_POLL)}
      />
    );
  } else if (pollState === POLL_STATE.EDITING_POLL) {
    return (
      <>
        {showRemoveModal && (
          <RemovePollModal
            title={trans("Discard poll?")}
            subTitle={trans("Your poll will be permanently removed")}
            removeLabel={trans("Discard")}
            onRemove={() => clearPollData()}
            onClose={() => setShowRemoveModal(false)}
          />
        )}
        <div className="writer-poll-container">
          <textarea
            className={classNames("writer-poll-title", {
              error: pollTitleError
            })}
            type="text"
            id="name"
            name="name"
            placeholder={trans("Ask your readers a question")}
            onChange={event => {
              setPollTitle(event.target.value);
              setPollTitleError("");
            }}
            onKeyDown={handleKeyDown}
          />
          {pollTitle?.length - POLL_TITLE_CHAR_MAX > 0 && (
            <div className="char-overflow-count" key={`title`}>
              {`-${pollTitle.length - POLL_TITLE_CHAR_MAX}`}
            </div>
          )}
          {pollOptions?.map((option, index) => (
            <React.Fragment key={`option-${index}`}>
              <div className="input-wrapper">
                <input
                  className={classNames("writer-poll-option", {
                    "valid-option": option?.valid,
                    error: option?.error
                  })}
                  type="text"
                  id={`option-${index}`}
                  name={`option-${index}`}
                  placeholder={option.label}
                  value={option?.value || ""}
                  onChange={event =>
                    onPollOptionChange(index, event.target.value)
                  }
                />
                {pollOptions.length > 2 && (
                  <button onClick={() => onPollOptionRemove(index)}>
                    <Icon name={IconNames.WpClose} size={20} />
                  </button>
                )}
              </div>
              {option?.charOverflowCount > 0 && (
                <div className="char-overflow-count">
                  {`-${option?.charOverflowCount}`}
                </div>
              )}
            </React.Fragment>
          ))}
          {pollOptions.length < 5 && (
            <div className="add-option-button">
              <Button
                variant={ButtonVariant.SECONDARY}
                label={trans("Add another answer")}
                ariaLabel={trans("Add another answer")}
                leadingIcon={IconNames.WpAdd}
                onClick={() =>
                  setPollOptions([
                    ...pollOptions,
                    { label: trans("Answer %s", pollOptions.length + 1) }
                  ])
                }
                fullWidth
              />
            </div>
          )}
          {(pollOptionError || pollTitleError) && (
            <div className="poll-error">
              <Icon name={IconNames.WpWarning} size={16} />
              {pollOptionError ? pollOptionError : pollTitleError}
            </div>
          )}
          <div className="poll-buttons">
            <Button
              variant={ButtonVariant.PRIMARY}
              label={trans("Add poll")}
              disabled={!pollIsValid}
              ariaLabel={trans("Add poll")}
              onClick={() => handleAddPoll()}
            />
            <Button
              variant={ButtonVariant.TERTIARY}
              label={trans("Discard poll")}
              ariaLabel={trans("Discard poll")}
              onClick={() => setShowRemoveModal(true)}
            />
          </div>
        </div>
      </>
    );
  } else if (pollState === POLL_STATE.VIEW_POLL) {
    return (
      <>
        {showRemoveModal && (
          <RemovePollModal
            title={trans("Permanently delete poll?")}
            subTitle={trans(
              "Your poll and any responses will be permanently removed from your story"
            )}
            removeLabel={trans("Delete")}
            onRemove={() => handleDeletePoll()}
            onClose={() => setShowRemoveModal(false)}
          />
        )}
        <div className="view-poll">
          <ReaderPoll
            storyId={Number(storyId)}
            partId={partId}
            pollData={viewPollData}
          />
          <Button
            variant={ButtonVariant.SECONDARY}
            label={trans("Delete poll")}
            ariaLabel={trans("Delete poll")}
            leadingIcon={IconNames.WpDelete}
            onClick={() => setShowRemoveModal(true)}
          />
        </div>
      </>
    );
  } else {
    return <></>;
  }
};

WriterPoll.propTypes = {
  storyId: PropTypes.string,
  partId: PropTypes.number
};

export default WriterPoll;
